import { useState, useEffect, useMemo } from "react";

import "styles/forcastsetting.css";
import { PARAM_NAMES } from "./data";
import { isControllableLever } from "./liftTemplates";

/**
 * DCA Management Parameter panel (P0).
 *
 * P0 纠偏 (§7.1 / §3.A): this panel now shows ONLY controllable levers as
 * tunable rows (unit + equipment limit + current value + AI suggested + user
 * override + constraint hint). State / result quantities (BHFP, water-cut,
 * GOR, THP…) are MOVED to a read-only diagnostics section and can never be
 * "optimized". Levers and their unit/limit/hint come from the resolved lift
 * template (§5.2). When the BE lift contract is absent everything still renders
 * via the static fallback (graceful degradation).
 *
 * props.value (extends the legacy forecastSetting shape):
 *   {
 *     forecastMonths,
 *     paramsNames:     [<all selected param keys, BE-positional order>],
 *     paramsRange:     [...],   // "min-max" history range, positional
 *     paramsSuggested: [...],   // AI suggested, positional
 *     paramsDefault:   [...],   // last observed value, positional
 *     paramsValues:    [...],   // last user input, positional
 *     liftTemplate:    { levers:[{key,name,unit,min,max,hint}], leverKeys, label, source, optimizationShape },
 *     beLeverKeys:     [...] | null,  // BE controllable_levers (override classifier)
 *   }
 *
 * onApply emits the SAME positional paramsValues array (aligned to paramsNames)
 * so the BE forecast call is unchanged — but state-quantity slots are pinned to
 * their observed default (never user-tunable), only lever slots carry overrides.
 */
function DcaManagementForecastSetting(props) {
  const { value, onApply, onClose } = props;
  const {
    paramsNames = [],
    paramsRange = [],
    paramsSuggested = [],
    paramsDefault = [],
    paramsValues = [],
    liftTemplate,
    beLeverKeys,
  } = value;

  const [forecastMonths, setForecastMonths] = useState(value.forecastMonths);

  // Partition the selected params into controllable levers vs state quantities,
  // preserving original index (for positional BE alignment on Apply).
  const { leverRows, stateRows } = useMemo(() => {
    const levers = [];
    const states = [];
    paramsNames.forEach((key, index) => {
      const meta = (liftTemplate?.levers || []).find((l) => l.key === key);
      const row = {
        key,
        index,
        name: meta?.name || PARAM_NAMES[key]?.name || key,
        unit:
          meta?.unit != null ? meta.unit : PARAM_NAMES[key]?.unit || "",
        min: meta?.min ?? null,
        max: meta?.max ?? null,
        hint: meta?.hint || "",
        range: paramsRange[index],
        suggested: paramsSuggested[index],
        default: paramsDefault[index],
        user: paramsValues[index],
      };
      if (isControllableLever(key, beLeverKeys)) {
        levers.push(row);
      } else {
        states.push(row);
      }
    });
    return { leverRows: levers, stateRows: states };
  }, [
    paramsNames,
    paramsRange,
    paramsSuggested,
    paramsDefault,
    paramsValues,
    liftTemplate,
    beLeverKeys,
  ]);

  // Editable user-override values, keyed by lever param key. Seed from last
  // user input, else AI suggested, else observed default.
  const [userValues, setUserValues] = useState({});
  useEffect(() => {
    const seed = {};
    leverRows.forEach((r) => {
      const v =
        r.user !== null && r.user !== undefined && r.user !== ""
          ? r.user
          : r.suggested ?? r.default ?? "";
      seed[r.key] = v;
    });
    setUserValues(seed);
    setForecastMonths(value.forecastMonths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsNames.join(","), value.forecastMonths]);

  const onChangeUser = (key, raw) => {
    setUserValues((prev) => ({ ...prev, [key]: raw }));
  };

  const clampHint = (row) => {
    const v = parseFloat(userValues[row.key]);
    if (Number.isNaN(v)) return null;
    if (row.min != null && v < row.min) return `below equipment min (${row.min})`;
    if (row.max != null && v > row.max) return `above equipment max (${row.max})`;
    return null;
  };

  const handleApply = () => {
    // Rebuild positional paramsValues aligned to paramsNames:
    //  - lever slots: user override
    //  - state-quantity slots: pinned to observed default (never optimized)
    const out = paramsNames.map((key, index) => {
      if (isControllableLever(key, beLeverKeys)) {
        const v = userValues[key];
        return v === "" || v === undefined ? null : v;
      }
      // state quantity → keep observed default, do NOT let it be a free knob
      return paramsDefault[index] ?? null;
    });
    onApply({
      forecastMonths,
      paramsValues: out,
    });
    onClose();
  };

  const shapeNote = liftTemplate?.optimizationShape
    ? `optimization shape: ${liftTemplate.optimizationShape}`
    : null;

  return (
    <div
      className="popup-wrapper d-flex flex-column"
      style={{ minWidth: "480px", maxHeight: "80vh", overflowY: "auto" }}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span>Parameter Setting (controllable levers)</span>
        <div onClick={() => onClose()} style={{ cursor: "pointer" }}>
          <span>X</span>
        </div>
      </div>

      {/* Lift banner — which lift template / lever set is in effect + source. */}
      {liftTemplate && (
        <div
          style={{
            fontSize: "12px",
            marginBottom: "6px",
            opacity: 0.85,
          }}
        >
          Lift: <b>{liftTemplate.label}</b>
          {shapeNote ? ` · ${shapeNote}` : ""}
          {liftTemplate.source === "backend" && " · levers from server"}
          {liftTemplate.source === "fallback" &&
            " · server template pending, using lift defaults"}
          {liftTemplate.source === "degraded" &&
            " · lift type unknown, conservative defaults"}
        </div>
      )}

      <div
        className="d-flex align-items-center"
        style={{ marginTop: "8px", marginBottom: "8px" }}
      >
        <label className="me-1">Forecast:</label>
        <input
          value={forecastMonths}
          name="months"
          type="number"
          style={{ width: "60px", height: "15px" }}
          onChange={(event) =>
            setForecastMonths(
              event.target.value < 0 ? 0 : event.target.value
            )
          }
        />
        <span className="ms-2">(Months)</span>
      </div>

      {/* Controllable levers — the ONLY tunable rows. */}
      <div className="fcs_container">
        <div style={{ fontWeight: 600, marginBottom: "4px" }}>
          Controllable levers
        </div>
        {leverRows.length === 0 ? (
          <div style={{ fontSize: "12px", opacity: 0.7 }}>
            No controllable lever selected for this lift type. Pick levers in
            Model Setting.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "start" }}>Lever</th>
                <th style={{ textAlign: "start" }}>Unit</th>
                <th style={{ textAlign: "start" }}>Equip. limit</th>
                <th style={{ textAlign: "start" }}>History</th>
                <th style={{ textAlign: "start" }}>Current</th>
                <th style={{ textAlign: "start" }}>AI suggest</th>
                <th style={{ textAlign: "start" }}>User override</th>
              </tr>
            </thead>
            <tbody>
              {leverRows.map((row) => {
                const warn = clampHint(row);
                return (
                  <tr key={row.key}>
                    <td title={row.hint}>{row.name}</td>
                    <td>{row.unit}</td>
                    <td>
                      {row.min != null && row.max != null
                        ? `${row.min}–${row.max}`
                        : "—"}
                    </td>
                    <td>{row.range ?? "—"}</td>
                    <td>{row.default ?? "—"}</td>
                    <td>{row.suggested ?? "—"}</td>
                    <td>
                      <input
                        className="dcm_input"
                        value={userValues[row.key] ?? ""}
                        type="text"
                        style={{ height: "16px", textAlign: "start", width: "70px" }}
                        onChange={(e) => onChangeUser(row.key, e.target.value)}
                      />
                      {warn && (
                        <div
                          style={{
                            color: "#FF8C00",
                            fontSize: "10px",
                            lineHeight: 1.1,
                          }}
                        >
                          {warn}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* State / result quantities — READ-ONLY diagnostics, never optimizable. */}
      {stateRows.length > 0 && (
        <div className="fcs_container" style={{ marginTop: "12px" }}>
          <div style={{ fontWeight: 600, marginBottom: "4px" }}>
            State / result (read-only diagnostics — not optimizable)
          </div>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "start" }}>Quantity</th>
                <th style={{ textAlign: "start" }}>Unit</th>
                <th style={{ textAlign: "start" }}>History</th>
                <th style={{ textAlign: "start" }}>Current</th>
              </tr>
            </thead>
            <tbody>
              {stateRows.map((row) => (
                <tr key={row.key}>
                  <td>{row.name}</td>
                  <td>{row.unit}</td>
                  <td>{row.range ?? "—"}</td>
                  <td>{row.default ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="forecast-lower-part d-flex flex-column mt-2">
        <div className="d-flex justify-content-between align-items-center mt-2">
          <button
            className="btn-custom"
            onClick={handleApply}
            disabled={!parseInt(forecastMonths)}
          >
            Apply
          </button>
          <button
            className="btn-custom"
            style={{ minWidth: "8em" }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default DcaManagementForecastSetting;
